import { filter, get } from 'lodash';
import { FEATURE_FLAGS } from 'utils/constants';
import { classOpsService } from 'utils/msEndpointConstants';
import { actionHandlerWithFeatureFlag, parseGraphQLError } from '../../utils';
import { SkApolloRequest } from '../../utils/apolloUtils';
import { MS_PHASE_2D_CLASS_OPERATIONS_ENDPOINTS } from '../../utils/msEndpointConstants';
import { FIND_ALL_APPOINTMENTS } from '../models/calenderModel';
import { MutationBuilder } from '../queryBuilder';
import { FETCH_ALL_CALENDAR_DATA, FETCH_APPOINTMENTS } from './actionTypes';
import DatadogHandler from '../../utils/datadog';
import SkRequest from './BaseRequest';
import {
  msFetchAppointments,
  msFindLeaves,
  msHolidaysOfYearForCalendar,
  msCreateAppointment,
} from '../../services/CalendarService';

const {
  EVENT: { GET_CALENDAR_EVENTS },
} = MS_PHASE_2D_CLASS_OPERATIONS_ENDPOINTS;

const _mMutationEnquiryBuilder = new MutationBuilder();

_mMutationEnquiryBuilder.setNewMutation('addCalendarEvent', {
  ID: 'number',
});

_mMutationEnquiryBuilder.setNewMutation('cancelAvailability', {});

const dispatchCalendarData = (
  dispatch,
  inProgress = true,
  data = {},
  error = null
) => {
  dispatch({
    type: FETCH_ALL_CALENDAR_DATA,
    key: 'allCalendarData',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchFetchAppointments = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: FETCH_APPOINTMENTS,
    key: 'appointments',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

export const monoFetchAppointments = async (dispatch, reqData) => {
  try {
    const data = await SkApolloRequest({
      params: {
        query: FIND_ALL_APPOINTMENTS,
        variables: reqData,
      },
      type: 'query',
    });

    return data;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(ex, {}, 'error');

    dispatchFetchAppointments(dispatch, false, null, {
      error: parseGraphQLError(ex),
    });
    return parseGraphQLError(ex);
  }
};

export const fetchAppointments = reqData => async dispatch => {
  dispatchFetchAppointments(dispatch);
  try {
    const data = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msFetchAppointments,
        monoHandler: monoFetchAppointments,
        extraData: reqData,
      }
    );

    if (data.success) {
      dispatchFetchAppointments(dispatch, false, { data: data.data });
    } else {
      dispatchFetchAppointments(dispatch, false, null, data.error);
    }

    return data.data;
  } catch (ex) {
    return ex;
  }
};

export const monoCreateAppointment = async (_, reqData) => {
  const query = _mMutationEnquiryBuilder.getMutationAsString(
    'addCalendarEvent',
    reqData
  );
  try {
    const data = await SkRequest({
      data: {
        query,
      },
      method: 'POST',
    });

    return data;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(ex, {}, 'error');

    return ex;
  }
};

export const createAppointment = reqData => async () =>
  actionHandlerWithFeatureFlag(FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED, {
    msHandler: msCreateAppointment,
    monoHandler: monoCreateAppointment,
    extraData: reqData,
  });

export const cancelAvailibility = reqData => async () => {
  const query = _mMutationEnquiryBuilder.getMutationAsString(
    'cancelAvailability',
    reqData
  );
  try {
    const data = await SkRequest({
      data: {
        query,
      },
      method: 'POST',
    });

    return data;
  } catch (ex) {
    return ex;
  }
};

const monoFetchAllEventsForCalendar = async (dispatch, { reqData }) => {
  try {
    const data = await SkApolloRequest({
      params: reqData,
      type: 'bulkQuery',
    });

    return data.data;
  } catch (ex) {
    dispatchCalendarData(dispatch, false, null, {
      error: parseGraphQLError(ex),
    });
  }
};

// Integrate Events For Calendar[MS]
const msFetchAllEventsForCalendar = async (dispatch, { reqData }) => {
  try {
    const _classOpsService = classOpsService();
    const calendarReqData = get(get(get(reqData, '1'), 'variables'), '0');
    const reqParam = {
      page: get(calendarReqData, 'pageParams.page', 1),
      perPage: get(calendarReqData, 'pageParams.perPage', 20),
      sort: '-updated_at',
      centreIds: get(calendarReqData, 'fkCentre', []),
      from: get(calendarReqData, 'from').split('+').at(0),
      to: get(calendarReqData, 'to').split('+').at(0),
    };

    const response = await _classOpsService.get(
      `${GET_CALENDAR_EVENTS}?${decodeURIComponent(
        new URLSearchParams(reqParam)
      )}`
    );

    const getAllEventsForCalendar = {
      ...response.data,
      __typename: 'ListEvents',
    };

    return {
      getAllEventsForCalendar,
    };
  } catch (ex) {
    dispatchCalendarData(dispatch, false, null, {
      error: ex,
    });
  }
};

export const msFetchAllCalendarData = async (_, { reqData }) => {
  const calendarEventReqData = (reqData || []).find(
    item => get(item, 'key') === 'findAllCalendarEvent'
  );
  const [findLeaves, getHolidaysOfYearForCalendar, findAllCalendarEvent] =
    await Promise.all([
      msFindLeaves(_, reqData),
      msHolidaysOfYearForCalendar(_, reqData),
      msFetchAppointments(_, get(calendarEventReqData, 'variables.0')),
    ]);

  return {
    findLeaves,
    getHolidaysOfYearForCalendar,
    findAllCalendarEvent: {
      ...get(findAllCalendarEvent, 'data.findAllCalendarEvent', {
        totalCount: 0,
        data: [],
      }),
    },
  };
};

export const fetchAllCalendarData = reqData => async dispatch => {
  dispatchCalendarData(dispatch);
  try {
    const phase2DReqData = filter(reqData, query => {
      return get(query, 'key') === 'getAllEventsForCalendar';
    });

    const data = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_2D_EVENT_RESOURCES_VISITOR_BUS_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msFetchAllEventsForCalendar,
        monoHandler: monoFetchAllEventsForCalendar,
        extraData: { reqData: phase2DReqData },
      }
    );

    const phase3ReqData = filter(reqData, query => {
      return get(query, 'key') !== 'getAllEventsForCalendar';
    });

    const allCalendarData = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msFetchAllCalendarData,
        monoHandler: monoFetchAllEventsForCalendar,
        extraData: { reqData: phase3ReqData },
      }
    );

    const calendarData = {
      ...data,
      ...allCalendarData,
    };

    dispatchCalendarData(dispatch, false, { data: calendarData });

    return calendarData;
  } catch (ex) {
    dispatchCalendarData(dispatch, false, null, {
      error: parseGraphQLError(ex),
    });
    return parseGraphQLError(ex);
  }
};
